import React, { useState, useEffect } from 'react';
import { getCleanerStatsDetailed, getVerificationStats, getBatteryStats, getBatteryHistory, getPortfolioAnalytics } from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line,
} from 'recharts';

/* ── colour helpers ─────────────────────────────────────────────── */
const COLORS = {
  green:  '#22c55e',
  yellow: '#eab308',
  red:    '#ef4444',
  blue:   '#3b82f6',
  purple: '#a855f7',
  orange: '#f97316',
  cyan:   '#06b6d4',
  pink:   '#ec4899',
};
const PIE_PALETTE = [COLORS.green, COLORS.red, COLORS.yellow];
const BAR_PALETTE = [COLORS.blue, COLORS.cyan, COLORS.purple, COLORS.orange, COLORS.pink, COLORS.green, COLORS.yellow];
const SCORE_COLORS = [COLORS.red, COLORS.yellow, '#86efac', COLORS.green];
const rateColor = (r) => r >= 80 ? COLORS.green : r >= 50 ? COLORS.yellow : COLORS.red;
const batteryColor = (b) => b > 50 ? COLORS.green : b > 20 ? COLORS.yellow : COLORS.red;

/* ── shared inline styles ───────────────────────────────────────── */
// Kept as a style object because it is spread across many call sites, but the
// values are the same as the .ayni-card class so the page matches the rest of the app.
const sectionCard = {
  backgroundColor: 'var(--bg-card)',
  borderRadius: '12px',
  border: '1px solid var(--border-default)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
  padding: '24px',
  marginBottom: '24px',
};

const th = {
  textAlign: 'left', padding: '12px 16px',
  color: 'var(--text-muted)', fontWeight: '600',
  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
  borderBottom: '1px solid var(--border-default)',
  backgroundColor: 'var(--bg-table-header)',
};
const thC = { ...th, textAlign: 'center' };
const td = {
  padding: '12px 16px', color: 'var(--text-primary)', fontSize: '14px',
  borderBottom: '1px solid var(--border-default)', verticalAlign: 'middle',
};
const tdC = { ...td, textAlign: 'center', color: 'var(--text-secondary)' };

/* ── small components ───────────────────────────────────────────── */
const StatCard = ({ label, value, sub, color, icon }) => (
  <div style={{
    flex: 1, minWidth: 0,
    background: 'var(--bg-page)',
    borderRadius: '12px', padding: '18px 22px',
    border: '1px solid var(--border-default)',
    display: 'flex', flexDirection: 'column', gap: '2px',
  }}>
    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
    <div style={{ fontSize: '32px', fontWeight: '700', color: color || 'var(--text-primary)', lineHeight: 1.1, marginTop: '4px' }}>
      {icon && <span style={{ marginRight: '6px', fontSize: '22px', verticalAlign: 'middle' }}>{icon}</span>}
      {value}
    </div>
    {sub && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{sub}</div>}
  </div>
);

const SectionHeader = ({ title, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
    <h2 className="ayni-display-md" style={{ margin: 0 }}>{title}</h2>
    {children}
  </div>
);

const ToggleGroup = ({ options, active, onChange }) => (
  <div className="ayni-seg">
    {options.map(o => (
      <button key={o.value} className={active === o.value ? 'on' : ''} onClick={() => onChange(o.value)}>
        {o.label}
      </button>
    ))}
  </div>
);

/* ── custom recharts tooltip ────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-light)',
      borderRadius: '8px', padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    }}>
      <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: '12px', color: p.color, display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: p.color, display: 'inline-block' }} />
          {p.name}: <strong>{p.value}{typeof p.value === 'number' && p.name.includes('Rate') ? '%' : ''}</strong>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════ */
const AnalyticsDashboard = () => {
  const [responseStats, setResponseStats] = useState(null);
  const [verificationStats, setVerificationStats] = useState(null);
  const [batteryData, setBatteryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responseView, setResponseView] = useState('cleaner');
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedVerRow, setExpandedVerRow] = useState(null);
  const [batteryHistory, setBatteryHistory] = useState(null);
  const [historyRange, setHistoryRange] = useState(30);
  const [batteryDeviceFilter, setBatteryDeviceFilter] = useState('all');
  const [reviewPage, setReviewPage] = useState(0);
  const REVIEW_PAGE_SIZE = 10;
  const [portfolio, setPortfolio] = useState(null);
  const [portfolioPropertyId, setPortfolioPropertyId] = useState('');
  const [portfolioDays, setPortfolioDays] = useState(30);

  const fetchPortfolio = (propId, days) => {
    const params = {};
    if (propId) params.property_id = propId;
    params.days = days;
    getPortfolioAnalytics(params).then(setPortfolio).catch(() => {});
  };

  useEffect(() => {
    Promise.all([
      getCleanerStatsDetailed().catch(() => null),
      getVerificationStats().catch(() => null),
    ]).then(([rs, vs]) => {
      setResponseStats(rs);
      setVerificationStats(vs);
      setLoading(false);
    });
    fetchPortfolio('', 30);
    getBatteryStats().then(setBatteryData).catch(() => {});
  }, []);

  useEffect(() => {
    getBatteryHistory(historyRange).then(setBatteryHistory).catch(() => {});
  }, [historyRange]);

  if (loading) {
    return (
      <div className="ayni-page">
        <div className="ayni-head">
          <h1 className="ayni-display-lg" style={{ margin: 0 }}>Dashboard</h1>
        </div>
        <div className="ayni-empty">Loading analytics...</div>
      </div>
    );
  }

  /* ── aggregate stats ── */
  const cleaners = responseStats?.cleaners || [];
  const properties = responseStats?.properties || [];

  const totalRequests   = cleaners.reduce((s, c) => s + c.total_requests, 0);
  const totalAccepted   = cleaners.reduce((s, c) => s + c.accepted, 0);
  const totalDeclined   = cleaners.reduce((s, c) => s + c.declined, 0);
  const totalNoReply    = cleaners.reduce((s, c) => s + c.no_reply, 0);
  const totalResponses  = totalAccepted + totalDeclined + totalNoReply;
  const overallAcceptance = totalResponses > 0 ? ((totalAccepted / totalResponses) * 100).toFixed(1) : '0';

  const verProps          = verificationStats?.properties || [];
  const totalVer          = verProps.reduce((s, p) => s + p.total_verifications, 0);
  const totalAutoApproved = verProps.reduce((s, p) => s + p.auto_approved, 0);
  const totalAdminInt     = verProps.reduce((s, p) => s + p.admin_intervention, 0);
  const overallPassRate   = totalVer > 0 ? ((totalAutoApproved / totalVer) * 100).toFixed(1) : '0';

  const toggleRow    = (k) => setExpandedRow(p => p === k ? null : k);
  const toggleVerRow = (k) => setExpandedVerRow(p => p === k ? null : k);

  /* ── chart data ── */
  // Bar chart: acceptance rate per cleaner (sorted desc)
  const cleanerBarData = [...cleaners]
    .filter(c => c.total_requests > 0)
    .sort((a, b) => b.acceptance_rate - a.acceptance_rate)
    .map(c => ({
      name: c.name?.split(' ')[0] || 'Unknown',  // first name for chart labels
      fullName: c.name,
      'Acceptance Rate': c.acceptance_rate,
      Accepted: c.accepted,
      Declined: c.declined,
      'No Reply': c.no_reply,
    }));

  // Pie chart: overall response distribution
  const responsePieData = [
    { name: 'Accepted',  value: totalAccepted },
    { name: 'Declined',  value: totalDeclined },
    { name: 'No Reply',  value: totalNoReply },
  ].filter(d => d.value > 0);

  // Bar chart: per-property acceptance
  const propertyBarData = properties.map(p => {
    const pAcc = p.cleaners.reduce((s, c) => s + c.accepted, 0);
    const pDec = p.cleaners.reduce((s, c) => s + c.declined, 0);
    const pNr  = p.cleaners.reduce((s, c) => s + c.no_reply, 0);
    const pTot = pAcc + pDec + pNr;
    return {
      name: p.property_name,
      'Acceptance Rate': pTot > 0 ? parseFloat(((pAcc / pTot) * 100).toFixed(1)) : 0,
      Accepted: pAcc,
      Declined: pDec,
      'No Reply': pNr,
    };
  }).sort((a, b) => b['Acceptance Rate'] - a['Acceptance Rate']);

  // Score distribution histogram
  const scoreDistData = (verificationStats?.score_distribution || []).map((bucket, i) => ({
    name: bucket.label,
    Count: bucket.count,
    fill: SCORE_COLORS[i] || COLORS.blue,
  }));

  // Verification bar: per-property avg score
  const verBarData = verProps
    .filter(p => p.total_verifications > 0)
    .sort((a, b) => b.avg_score - a.avg_score)
    .map(p => ({
      name: p.property_name,
      'Avg Score': p.avg_score,
      'Pass Rate': p.pass_rate,
      Total: p.total_verifications,
    }));

  const chartAxisStyle = { fontSize: 11, fill: 'var(--text-secondary)' };

  return (
    <div className="ayni-page">
      <div className="ayni-head" style={{ marginBottom: 28 }}>
        <h1 className="ayni-display-lg" style={{ margin: 0 }}>Dashboard</h1>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          PORTFOLIO ANALYTICS
          ═══════════════════════════════════════════════════════════ */}
      {portfolio && (
        <div style={sectionCard}>
          <SectionHeader title="Portfolio Analytics">
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Property Filter */}
              <select
                value={portfolioPropertyId}
                onChange={(e) => { setPortfolioPropertyId(e.target.value); fetchPortfolio(e.target.value, portfolioDays); }}
                style={{
                  padding: '6px 12px', fontSize: '12px', fontWeight: '600',
                  border: '1px solid var(--border-light)', borderRadius: '8px',
                  backgroundColor: 'var(--bg-page)', color: 'var(--text-primary)',
                  cursor: 'pointer', outline: 'none',
                }}
              >
                <option value="">All Properties</option>
                {(portfolio.properties || []).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

              {/* Time Range Toggle */}
              <ToggleGroup
                options={[
                  { value: 7, label: '7d' },
                  { value: 30, label: '30d' },
                  { value: 90, label: '90d' },
                  { value: 0, label: 'All' },
                ]}
                active={portfolioDays}
                onChange={(v) => { setPortfolioDays(v); fetchPortfolio(portfolioPropertyId, v); }}
              />
            </div>
          </SectionHeader>

          {/* KPI Cards */}
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '24px' }}>
            <StatCard label="OCCUPANCY" value={`${portfolio.occupancy?.rate || 0}%`} sub={`${portfolio.occupancy?.occupied_days || 0} of ${portfolio.occupancy?.total_days || 0} days`} color={COLORS.blue} />
            <StatCard label="RESERVATIONS" value={portfolio.reservations || 0} sub={portfolio.range_label || ''} color={COLORS.green} />
            <StatCard label="CLEANINGS" value={portfolio.cleanings || 0} sub={portfolio.range_label || ''} color={COLORS.purple} />
            <StatCard label="AVG SCORE" value={portfolio.avg_score ? `${portfolio.avg_score}%` : '—'} color={portfolio.avg_score >= 85 ? COLORS.green : COLORS.yellow} />
          </div>

          {/* Charts Row */}
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            {/* Monthly Trend */}
            {portfolio.monthly_trend?.length > 0 && (
              <div style={{ flex: 2, minWidth: '300px', background: 'var(--bg-page)', borderRadius: '12px', padding: '20px', border: '1px solid var(--border-light)' }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '16px' }}>Monthly Cleanings</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={portfolio.monthly_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
                    <YAxis tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="cleanings" name="Cleanings" fill={COLORS.orange} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Top Cleaners */}
            {portfolio.top_cleaners?.length > 0 && (
              <div style={{ flex: 1, minWidth: '220px', background: 'var(--bg-page)', borderRadius: '12px', padding: '20px', border: '1px solid var(--border-light)' }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '16px' }}>Top Aynistas</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {portfolio.top_cleaners.map((c, i) => {
                    const avatarColors = [COLORS.orange, COLORS.purple, COLORS.pink, COLORS.cyan, COLORS.blue];
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: '50%',
                          backgroundColor: avatarColors[i % avatarColors.length],
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '14px', fontWeight: '700', color: '#fff',
                        }}>{c.initials}</div>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>{c.name}</div>
                          <div style={{ fontSize: '12px', color: COLORS.green, fontWeight: '600' }}>{c.avg_score}% avg</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          SECTION 1: Cleaner Response Rates
          ═══════════════════════════════════════════════════════════ */}
      <div style={sectionCard}>
        <SectionHeader title="Aynista Response Rates">
          <ToggleGroup
            options={[{ value: 'cleaner', label: 'By Aynista' }, { value: 'property', label: 'By Property' }]}
            active={responseView}
            onChange={(v) => { setResponseView(v); setExpandedRow(null); }}
          />
        </SectionHeader>

        {/* Summary cards */}
        <div style={{ display: 'flex', gap: '14px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <StatCard label="Total Requests" value={totalRequests} />
          <StatCard label="Accepted" value={totalAccepted} color={COLORS.green} />
          <StatCard label="Declined" value={totalDeclined} color={COLORS.red} />
          <StatCard label="No Reply" value={totalNoReply} color={COLORS.yellow} />
          <StatCard label="Acceptance Rate" value={`${overallAcceptance}%`} color={rateColor(parseFloat(overallAcceptance))} />
        </div>

        {/* Charts row */}
        <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {/* Bar chart */}
          <div style={{
            flex: '2 1 400px', minHeight: 280,
            backgroundColor: 'var(--bg-page)', borderRadius: '12px',
            border: '1px solid var(--border-light)', padding: '16px',
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>
              {responseView === 'cleaner' ? 'Acceptance Rate by Aynista' : 'Acceptance Rate by Property'}
            </div>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={responseView === 'cleaner' ? cleanerBarData : propertyBarData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                <XAxis type="number" domain={[0, 100]} tick={chartAxisStyle} tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="name" width={90} tick={chartAxisStyle} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="Acceptance Rate" radius={[0, 6, 6, 0]} maxBarSize={28}>
                  {(responseView === 'cleaner' ? cleanerBarData : propertyBarData).map((entry, i) => (
                    <Cell key={i} fill={rateColor(entry['Acceptance Rate'])} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Pie chart */}
          {responsePieData.length > 0 && (
            <div style={{
              flex: '1 1 260px', minHeight: 280,
              backgroundColor: 'var(--bg-page)', borderRadius: '12px',
              border: '1px solid var(--border-light)', padding: '16px',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px', alignSelf: 'flex-start' }}>Response Distribution</div>
              <ResponsiveContainer width="100%" height={230}>
                <PieChart>
                  <Pie
                    data={responsePieData}
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                    label={false}
                  >
                    {responsePieData.map((_, i) => (
                      <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} (${name})`, '']} />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="circle"
                    iconSize={8}
                    formatter={(value, entry) => {
                      const pct = entry.payload?.percent ? `${(entry.payload.percent * 100).toFixed(0)}%` : '';
                      return `${value} ${pct}`;
                    }}
                    wrapperStyle={{ fontSize: '11px', lineHeight: '18px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Data table */}
        {responseView === 'cleaner' && cleaners.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                <th style={th}>Aynista</th>
                <th style={thC}>Requests</th>
                <th style={thC}>Accepted</th>
                <th style={thC}>Declined</th>
                <th style={thC}>No Reply</th>
                <th style={thC}>Acceptance Rate</th>
                <th style={thC}>Avg Response</th>
              </tr>
            </thead>
            <tbody>
              {cleaners.map((c, i) => (
                <React.Fragment key={i}>
                  <tr style={{ borderBottom: '1px solid var(--border-light)', cursor: c.properties?.length > 0 ? 'pointer' : 'default' }} onClick={() => c.properties?.length > 0 && toggleRow(i)}>
                    <td style={{ ...td, fontWeight: '500' }}>
                      {c.properties?.length > 0 && <span style={{ marginRight: '6px', fontSize: '10px', color: 'var(--text-secondary)' }}>{expandedRow === i ? '▼' : '▶'}</span>}
                      {c.name}
                    </td>
                    <td style={tdC}>{c.total_requests}</td>
                    <td style={{ ...tdC, color: COLORS.green, fontWeight: '500' }}>{c.accepted}</td>
                    <td style={{ ...tdC, color: COLORS.red }}>{c.declined}</td>
                    <td style={{ ...tdC, color: COLORS.yellow }}>{c.no_reply}</td>
                    <td style={tdC}>
                      <span style={{
                        fontWeight: '600', color: rateColor(c.acceptance_rate),
                        backgroundColor: `${rateColor(c.acceptance_rate)}15`,
                        padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
                      }}>{c.acceptance_rate}%</span>
                    </td>
                    <td style={tdC}>{c.avg_response_time_seconds != null ? `${c.avg_response_time_seconds}s` : '\u2014'}</td>
                  </tr>
                  {expandedRow === i && c.properties?.map((p, j) => (
                    <tr key={`${i}-${j}`} style={{ backgroundColor: 'var(--bg-page)', borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ ...td, paddingLeft: '36px', color: 'var(--text-secondary)', fontSize: '12px' }}>{p.property_name}</td>
                      <td style={tdC}>{p.total_requests}</td>
                      <td style={{ ...tdC, color: COLORS.green }}>{p.accepted}</td>
                      <td style={{ ...tdC, color: COLORS.red }}>{p.declined}</td>
                      <td style={{ ...tdC, color: COLORS.yellow }}>{p.no_reply}</td>
                      <td style={tdC}><span style={{ fontWeight: '500', color: rateColor(p.acceptance_rate) }}>{p.acceptance_rate}%</span></td>
                      <td style={tdC}>{p.avg_response_time_seconds != null ? `${p.avg_response_time_seconds}s` : '\u2014'}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        {responseView === 'property' && properties.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                <th style={th}>Property</th>
                <th style={thC}>Requests</th>
                <th style={thC}>Accepted</th>
                <th style={thC}>Declined</th>
                <th style={thC}>No Reply</th>
                <th style={thC}>Acceptance Rate</th>
                <th style={thC}>Avg Response</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((p, i) => {
                const pAcc = p.cleaners.reduce((s, c) => s + c.accepted, 0);
                const pDec = p.cleaners.reduce((s, c) => s + c.declined, 0);
                const pNr  = p.cleaners.reduce((s, c) => s + c.no_reply, 0);
                const pTot = pAcc + pDec + pNr;
                const pRate = pTot > 0 ? ((pAcc / pTot) * 100).toFixed(1) : '0';
                return (
                  <React.Fragment key={i}>
                    <tr style={{ borderBottom: '1px solid var(--border-light)', cursor: 'pointer' }} onClick={() => toggleRow(i)}>
                      <td style={{ ...td, fontWeight: '500' }}>
                        <span style={{ marginRight: '6px', fontSize: '10px', color: 'var(--text-secondary)' }}>{expandedRow === i ? '▼' : '▶'}</span>
                        {p.property_name}
                      </td>
                      <td style={tdC}>{pAcc + pDec + pNr}</td>
                      <td style={{ ...tdC, color: COLORS.green, fontWeight: '500' }}>{pAcc}</td>
                      <td style={{ ...tdC, color: COLORS.red }}>{pDec}</td>
                      <td style={{ ...tdC, color: COLORS.yellow }}>{pNr}</td>
                      <td style={tdC}>
                        <span style={{
                          fontWeight: '600', color: rateColor(parseFloat(pRate)),
                          backgroundColor: `${rateColor(parseFloat(pRate))}15`,
                          padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
                        }}>{pRate}%</span>
                      </td>
                      <td style={tdC}>{'\u2014'}</td>
                    </tr>
                    {expandedRow === i && p.cleaners.map((c, j) => (
                      <tr key={`${i}-${j}`} style={{ backgroundColor: 'var(--bg-page)', borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ ...td, paddingLeft: '36px', color: 'var(--text-secondary)', fontSize: '12px' }}>{c.cleaner_name}</td>
                        <td style={tdC}>{c.total_requests}</td>
                        <td style={{ ...tdC, color: COLORS.green }}>{c.accepted}</td>
                        <td style={{ ...tdC, color: COLORS.red }}>{c.declined}</td>
                        <td style={{ ...tdC, color: COLORS.yellow }}>{c.no_reply}</td>
                        <td style={tdC}><span style={{ fontWeight: '500', color: rateColor(c.acceptance_rate) }}>{c.acceptance_rate}%</span></td>
                        <td style={tdC}>{c.avg_response_time_seconds != null ? `${c.avg_response_time_seconds}s` : '\u2014'}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {!cleaners.length && !properties.length && (
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', padding: '20px 0', textAlign: 'center' }}>No dispatch data available yet.</div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 2: Verification Pass Rates
          ═══════════════════════════════════════════════════════════ */}
      <div style={sectionCard}>
        <SectionHeader title="Verification Pass Rates" />

        {/* Summary cards */}
        <div style={{ display: 'flex', gap: '14px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <StatCard label="Total Verifications" value={totalVer} />
          <StatCard label="Auto Approved" value={totalAutoApproved} sub={totalVer > 0 ? `${overallPassRate}% pass rate` : ''} color={COLORS.green} />
          <StatCard label="Admin Intervention" value={totalAdminInt} sub={totalVer > 0 ? `${((totalAdminInt / totalVer) * 100).toFixed(1)}%` : ''} color={COLORS.yellow} />
        </div>

        {/* Charts row */}
        {(verBarData.length > 0 || scoreDistData.some(d => d.Count > 0)) && (
          <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
            {/* Avg score bar chart */}
            {verBarData.length > 0 && (
              <div style={{
                flex: '2 1 400px', minHeight: 280,
                backgroundColor: 'var(--bg-page)', borderRadius: '12px',
                border: '1px solid var(--border-light)', padding: '16px',
              }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>Average Score by Property</div>
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={verBarData} margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                    <XAxis dataKey="name" tick={chartAxisStyle} interval={0} angle={-25} textAnchor="end" height={60} />
                    <YAxis domain={[0, 100]} tick={chartAxisStyle} tickFormatter={v => `${v}`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="Avg Score" radius={[6, 6, 0, 0]} maxBarSize={48}>
                      {verBarData.map((entry, i) => (
                        <Cell key={i} fill={rateColor(entry['Avg Score'])} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Score Distribution */}
            {scoreDistData.some(d => d.Count > 0) && (
              <div style={{
                flex: '1 1 260px', minHeight: 280,
                backgroundColor: 'var(--bg-page)', borderRadius: '12px',
                border: '1px solid var(--border-light)', padding: '16px',
                display: 'flex', flexDirection: 'column',
              }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>Score Distribution</div>
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={scoreDistData} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                    <XAxis dataKey="name" tick={chartAxisStyle} />
                    <YAxis tick={chartAxisStyle} allowDecimals={false} />
                    <Tooltip content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div style={{
                          backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-light)',
                          borderRadius: '8px', padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>{label}</div>
                          <div style={{ fontSize: '12px', color: payload[0]?.payload?.fill }}>
                            Verifications: <strong>{payload[0]?.value}</strong>
                          </div>
                        </div>
                      );
                    }} />
                    <Bar dataKey="Count" radius={[6, 6, 0, 0]} maxBarSize={56}>
                      {scoreDistData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Per-property table */}
        {verProps.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                <th style={th}>Property</th>
                <th style={thC}>Total</th>
                <th style={thC}>Auto Approved</th>
                <th style={thC}>Admin Intervention</th>
                <th style={thC}>Pass Rate</th>
                <th style={thC}>Avg Score</th>
              </tr>
            </thead>
            <tbody>
              {verProps.map((p, i) => (
                <React.Fragment key={i}>
                  <tr style={{ borderBottom: '1px solid var(--border-light)', cursor: p.cleaners?.length > 0 ? 'pointer' : 'default' }} onClick={() => p.cleaners?.length > 0 && toggleVerRow(i)}>
                    <td style={{ ...td, fontWeight: '500' }}>
                      {p.cleaners?.length > 0 && <span style={{ marginRight: '6px', fontSize: '10px', color: 'var(--text-secondary)' }}>{expandedVerRow === i ? '▼' : '▶'}</span>}
                      {p.property_name}
                    </td>
                    <td style={tdC}>{p.total_verifications}</td>
                    <td style={{ ...tdC, color: COLORS.green, fontWeight: '500' }}>{p.auto_approved}</td>
                    <td style={{ ...tdC, color: COLORS.yellow }}>{p.admin_intervention}</td>
                    <td style={tdC}>
                      <span style={{
                        fontWeight: '600', color: rateColor(p.pass_rate),
                        backgroundColor: `${rateColor(p.pass_rate)}15`,
                        padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
                      }}>{p.pass_rate}%</span>
                    </td>
                    <td style={tdC}><span style={{ fontWeight: '500' }}>{p.avg_score}/100</span></td>
                  </tr>
                  {expandedVerRow === i && p.cleaners?.map((c, j) => (
                    <tr key={`${i}-${j}`} style={{ backgroundColor: 'var(--bg-page)', borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ ...td, paddingLeft: '36px', color: 'var(--text-secondary)', fontSize: '12px' }}>{c.cleaner_name}</td>
                      <td style={tdC}>{c.total}</td>
                      <td style={{ ...tdC, color: COLORS.green }}>{c.auto_approved}</td>
                      <td style={{ ...tdC, color: COLORS.yellow }}>{c.admin_intervention}</td>
                      <td style={tdC}><span style={{ fontWeight: '500', color: rateColor(c.pass_rate) }}>{c.pass_rate}%</span></td>
                      <td style={tdC}>{c.avg_score}/100</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        {/* Spaces Requiring Admin Review */}
        {verificationStats?.admin_review_spaces?.length > 0 && (() => {
          const allItems = verificationStats.admin_review_spaces;
          const totalPages = Math.ceil(allItems.length / REVIEW_PAGE_SIZE);
          const pageItems = allItems.slice(reviewPage * REVIEW_PAGE_SIZE, (reviewPage + 1) * REVIEW_PAGE_SIZE);
          return (
          <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '20px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', margin: '0 0 16px 0' }}>
              Spaces Requiring Admin Review
              <span style={{ fontWeight: '400', fontSize: '12px', color: 'var(--text-secondary)', marginLeft: '8px' }}>({allItems.length})</span>
            </h3>
            <div style={{
              backgroundColor: 'var(--bg-page)', borderRadius: '12px',
              border: '1px solid var(--border-light)', padding: '16px', overflowX: 'auto',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                    <th style={th}>Date</th>
                    <th style={th}>Space</th>
                    <th style={th}>Property</th>
                    <th style={thC}>Score</th>
                    <th style={th}>AI Notes</th>
                    <th style={thC}>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((s, i) => {
                    const statusConfig = {
                      REVIEW_REQUIRED: { label: 'Pending Review', color: COLORS.yellow },
                      ESCALATED:       { label: 'Escalated',      color: COLORS.orange },
                      ADMIN_APPROVED:  { label: 'Admin Approved', color: COLORS.green },
                      ADMIN_REJECTED:  { label: 'Admin Rejected', color: COLORS.red },
                    };
                    const sc = statusConfig[s.status] || { label: s.status, color: 'var(--text-secondary)' };
                    const dateStr = s.scored_at ? new Date(s.scored_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '\u2014';
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-secondary)', fontSize: '12px' }}>{dateStr}</td>
                        <td style={{ ...td, fontWeight: '500' }}>{s.space_name}</td>
                        <td style={td}>{s.property_name}</td>
                        <td style={tdC}>
                          {s.score != null ? (
                            <span style={{
                              fontWeight: '600', color: rateColor(s.score),
                              backgroundColor: `${rateColor(s.score)}15`,
                              padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
                            }}>{s.score}/100</span>
                          ) : '\u2014'}
                        </td>
                        <td style={{ ...td, maxWidth: '300px', color: 'var(--text-secondary)', fontSize: '12px', lineHeight: '1.4' }}>
                          {s.ai_notes ? (s.ai_notes.length > 120 ? s.ai_notes.slice(0, 120) + '...' : s.ai_notes) : '\u2014'}
                        </td>
                        <td style={tdC}>
                          <span style={{
                            fontWeight: '600', fontSize: '11px',
                            color: sc.color,
                            backgroundColor: `${sc.color}15`,
                            padding: '3px 10px', borderRadius: '12px',
                            whiteSpace: 'nowrap',
                          }}>{sc.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
                  <button
                    onClick={() => setReviewPage(p => Math.max(0, p - 1))}
                    disabled={reviewPage === 0}
                    style={{
                      padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '500',
                      border: '1px solid var(--border-light)', backgroundColor: 'var(--bg-card)',
                      color: reviewPage === 0 ? 'var(--text-faint)' : 'var(--text-primary)',
                      cursor: reviewPage === 0 ? 'default' : 'pointer',
                    }}
                  >Prev</button>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {reviewPage + 1} / {totalPages}
                  </span>
                  <button
                    onClick={() => setReviewPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={reviewPage >= totalPages - 1}
                    style={{
                      padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '500',
                      border: '1px solid var(--border-light)', backgroundColor: 'var(--bg-card)',
                      color: reviewPage >= totalPages - 1 ? 'var(--text-faint)' : 'var(--text-primary)',
                      cursor: reviewPage >= totalPages - 1 ? 'default' : 'pointer',
                    }}
                  >Next</button>
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {!verProps.length && (
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', padding: '20px 0', textAlign: 'center' }}>No verification data available yet.</div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 3: Lock Battery Levels
          ═══════════════════════════════════════════════════════════ */}
      {(() => {
        const batteryProps = (batteryData?.properties || []).filter(p => p.lock != null);
        if (batteryProps.length === 0) return null;

        const totalLocks = batteryProps.length;
        const avgBattery = Math.round(batteryProps.reduce((s, p) => s + (p.lock.battery_percent || 0), 0) / totalLocks);
        const LOW_THRESHOLD = 20;
        const lowBatteryCount = batteryProps.filter(p => (p.lock.battery_percent || 0) < LOW_THRESHOLD).length;

        /* ── Battery bar chart data (current snapshot) ── */
        const batteryBarData = [...batteryProps]
          .sort((a, b) => (a.lock.battery_percent || 0) - (b.lock.battery_percent || 0))
          .map(p => ({
            name: p.property_name,
            'Battery %': p.lock.battery_percent || 0,
          }));

        /* ── History line chart data ── */
        const historyDevices = batteryHistory?.devices || [];
        const hasHistory = historyDevices.length > 0 && historyDevices.some(d => d.readings?.length >= 1);

        // Build unified timeline: { date: "Mar 10", "NINA 308": 85, "Test NINA": 48, ... }
        let lineChartData = [];
        const deviceNames = [];
        if (hasHistory) {
          const dateMap = {};
          const isHourly = historyRange === 1;
          historyDevices.forEach(device => {
            const label = device.property_name || device.device_name || device.device_id;
            if (!deviceNames.includes(label)) deviceNames.push(label);
            (device.readings || []).forEach(r => {
              const d = new Date(r.recorded_at);
              const key = isHourly ? d.toISOString().slice(0, 13) : d.toISOString().slice(0, 10);
              const shortLabel = isHourly
                ? d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
                : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              if (!dateMap[key]) dateMap[key] = { date: shortLabel, _sort: key };
              dateMap[key][label] = r.battery_percent;
            });
          });
          lineChartData = Object.values(dateMap).sort((a, b) => a._sort.localeCompare(b._sort));
        }

        /* ── Est. Days to Replace calculation ── */
        let estDaysToReplace = null;
        if (hasHistory) {
          const drainRates = [];
          historyDevices.forEach(device => {
            const readings = device.readings || [];
            if (readings.length < 2) return;
            const first = readings[0];
            const last = readings[readings.length - 1];
            const daysDiff = (new Date(last.recorded_at) - new Date(first.recorded_at)) / (1000 * 60 * 60 * 24);
            if (daysDiff <= 0) return;
            const drain = first.battery_percent - last.battery_percent;
            if (drain > 0) {
              const ratePerDay = drain / daysDiff;
              const currentBattery = last.battery_percent;
              const daysLeft = currentBattery > 5 ? (currentBattery - 5) / ratePerDay : 0;
              drainRates.push(daysLeft);
            }
          });
          if (drainRates.length > 0) {
            estDaysToReplace = Math.round(drainRates.reduce((s, v) => s + v, 0) / drainRates.length);
          }
        }

        /* ── Lock status table data ── */
        const lockStatusRows = batteryProps.map(p => {
          const histDevice = historyDevices.find(d => d.property_id === p.property_id || d.property_name === p.property_name);
          const lastReading = histDevice?.readings?.length > 0 ? histDevice.readings[histDevice.readings.length - 1] : null;
          return {
            property: p.property_name,
            lockName: p.lock.device_name || p.lock.name || 'Smart Lock',
            battery: p.lock.battery_percent || 0,
            online: lastReading?.online ?? p.lock.online ?? null,
            lastRecorded: lastReading?.recorded_at || p.lock.last_reading_at || null,
          };
        });

        return (
          <div style={sectionCard}>
            <SectionHeader title="Lock Battery Levels" />

            {/* Summary cards */}
            <div style={{ display: 'flex', gap: '14px', marginBottom: '24px', flexWrap: 'wrap' }}>
              <StatCard label="Total Locks" value={totalLocks} />
              <StatCard label="Avg Battery %" value={`${avgBattery}%`} color={batteryColor(avgBattery)} />
              <StatCard label="Low Battery" value={lowBatteryCount} sub={`< ${LOW_THRESHOLD}%`} color={lowBatteryCount > 0 ? COLORS.red : COLORS.green} />
              <StatCard
                label="Est. Days to Replace"
                value={estDaysToReplace != null ? estDaysToReplace : '\u2014'}
                sub={estDaysToReplace != null ? 'avg across locks' : 'needs more data'}
                color={estDaysToReplace != null && estDaysToReplace < 30 ? COLORS.red : estDaysToReplace != null && estDaysToReplace < 90 ? COLORS.yellow : COLORS.green}
              />
            </div>

            {/* Two-column chart area */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>

              {/* Left: Battery Drainage Line Chart (2/3 width) */}
              <div style={{
                flex: '2 1 500px', minHeight: 320,
                backgroundColor: 'var(--bg-page)', borderRadius: '12px',
                border: '1px solid var(--border-light)', padding: '16px',
                display: 'flex', flexDirection: 'column',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>Battery Drainage Over Time</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {deviceNames.length > 1 && (
                      <select
                        value={batteryDeviceFilter}
                        onChange={(e) => setBatteryDeviceFilter(e.target.value)}
                        style={{
                          padding: '5px 8px', fontSize: '11px', fontWeight: '500',
                          borderRadius: '6px', border: '1px solid var(--border-light)',
                          backgroundColor: 'var(--bg-input)', color: 'var(--text-secondary)',
                          cursor: 'pointer', outline: 'none',
                        }}
                      >
                        <option value="all">All Locks</option>
                        {deviceNames.map(name => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    )}
                    <ToggleGroup
                      options={[
                        { value: 1, label: '1d' },
                        { value: 7, label: '7d' },
                        { value: 30, label: '30d' },
                        { value: 90, label: '90d' },
                      ]}
                      active={historyRange}
                      onChange={(v) => setHistoryRange(v)}
                    />
                  </div>
                </div>
                {hasHistory && lineChartData.length >= 1 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={lineChartData} margin={{ left: 5, right: 20, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                      <XAxis dataKey="date" tick={chartAxisStyle} interval="preserveStartEnd" />
                      <YAxis domain={[0, 100]} tick={chartAxisStyle} tickFormatter={v => `${v}%`} />
                      <Tooltip content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div style={{
                            backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-light)',
                            borderRadius: '8px', padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }}>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>{label}</div>
                            {payload.map((p, i) => (
                              <div key={i} style={{ fontSize: '12px', color: p.color, display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: p.color, display: 'inline-block' }} />
                                {p.name}: <strong>{p.value}%</strong>
                              </div>
                            ))}
                          </div>
                        );
                      }} />
                      {deviceNames.filter(name => batteryDeviceFilter === 'all' || name === batteryDeviceFilter).map((name, i) => (
                        <Line
                          key={name}
                          type="monotone"
                          dataKey={name}
                          stroke={BAR_PALETTE[deviceNames.indexOf(name) % BAR_PALETTE.length]}
                          strokeWidth={2}
                          dot={{ r: 3, fill: BAR_PALETTE[deviceNames.indexOf(name) % BAR_PALETTE.length] }}
                          activeDot={{ r: 5 }}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'center', padding: '40px 20px',
                  }}>
                    Battery tracking started. Readings will appear here over time.
                  </div>
                )}
              </div>

              {/* Right: Current Battery Gauge Bar Chart (1/3 width) */}
              <div style={{
                flex: '1 1 260px', minHeight: 320,
                backgroundColor: 'var(--bg-page)', borderRadius: '12px',
                border: '1px solid var(--border-light)', padding: '16px',
                display: 'flex', flexDirection: 'column',
              }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>Current Battery Levels</div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
                  {batteryBarData.map((entry, i) => {
                    const pct = entry['Battery %'];
                    return (
                      <div key={i}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: '500' }}>{entry.name}</span>
                          <span style={{ fontSize: '12px', fontWeight: '700', color: batteryColor(pct) }}>{pct}%</span>
                        </div>
                        <div style={{
                          width: '100%', height: '10px',
                          backgroundColor: 'var(--border-light)', borderRadius: '5px',
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${pct}%`, height: '100%',
                            backgroundColor: batteryColor(pct), borderRadius: '5px',
                            transition: 'width 0.4s ease',
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Lock Status Mini-Table */}
            {lockStatusRows.length > 0 && (
              <div style={{
                backgroundColor: 'var(--bg-page)', borderRadius: '12px',
                border: '1px solid var(--border-light)', padding: '16px',
              }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>Lock Status</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                      <th style={th}>Property</th>
                      <th style={th}>Lock Name</th>
                      <th style={thC}>Battery %</th>
                      <th style={thC}>Status</th>
                      <th style={thC}>Last Recorded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lockStatusRows.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ ...td, fontWeight: '500' }}>{row.property}</td>
                        <td style={td}>{row.lockName}</td>
                        <td style={tdC}>
                          <span style={{
                            fontWeight: '600', color: batteryColor(row.battery),
                            backgroundColor: `${batteryColor(row.battery)}15`,
                            padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
                          }}>{row.battery}%</span>
                        </td>
                        <td style={tdC}>
                          {row.online != null ? (
                            <span style={{
                              fontWeight: '600', fontSize: '12px',
                              color: row.online ? COLORS.green : COLORS.red,
                              backgroundColor: row.online ? `${COLORS.green}15` : `${COLORS.red}15`,
                              padding: '3px 10px', borderRadius: '12px',
                            }}>
                              {row.online ? 'Online' : 'Offline'}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{'\u2014'}</span>
                          )}
                        </td>
                        <td style={tdC}>
                          {row.lastRecorded ? new Date(row.lastRecorded).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '\u2014'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default AnalyticsDashboard;
